import React from "react";
import CustomSearchableSelect from "components/Selects/CustomSearchableSelect";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import TableHeader from "components/Tables/TableHeader";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import { useParams, useHistory } from "react-router";
import CustomDatePicker from "components/CustomDates/CustomDatePicker";
import useStaffUsers from "hooks/useStaffUsers";
import { apiErrorHandler } from "helpers/apiErrorHandler";
import useAxios from "axios/httpService";
import {
  AddInquiryParticipantsUrl,
  SaveRespondentsDraftUrl,
  ParseEmailsUrl,
  UploadBulkEmailFileUrl,
} from "axios/customerAdminAPIsUrls";
import { GetRespondentsbyId } from "axios/customerAdminAPIsUrls";
import { validateEmail, validateDate, isEmpty } from "helpers/validations";
import { NotificationManager } from "components/common/ReactNotifications";
import Loading from "components/Loading/Loading";
import jwtDecode from "jwt-decode";
import CustomSwitchLabel from "components/Checkboxes/CustomSwitchLabel";
import Button from "components/CustomButtons/Button";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";

const SelectAndManageRespondents = () => {
  const min = new Date();
  console.log(min);
  const current = new Date();

  // it adds 2 days to a current date
  current.setDate(current.getDate() + 28);
  let tempdate = current.toDateString();
  const DefaultDate = new Date(tempdate + " " + min.toTimeString());

  // it adds 2 days to a current date
  min.setDate(min.getDate() + 1);
  let tempdate2 = min.toDateString();
  const minDate = new Date(tempdate2 + " " + min.toTimeString());

  const [fields, setFields] = React.useState({
    emails: [],
    numberOfParticipants: 0,
    expiryDate: DefaultDate,
    isCustomerAdminRespondent: false,
  });

  const [emailInput, setEmailInput] = React.useState("");

  const [inquiryRespondents, setInquiryRespondents] = React.useState([]);
  const [uploadedFile, setUploadedFile] = React.useState(null);
  const fileInputRef = React.useRef();
  /** Emails already persisted as Draft (from API); excluded from SaveRespondentsDraft payload */
  const initialDraftEmailsRef = React.useRef(new Set());

  const history = useHistory();

  const { inquiryId } = useParams();

  const [shouldValidateFields, setValidateFields] = React.useState(false);

  const token = localStorage.getItem("token");

  const decodedToken = jwtDecode(token);

  const { loading, error, customerUsers } = useStaffUsers(
    decodedToken.customerid
  );

  if (error) {
    console.log("Error in Getting Customer Users", error);
    apiErrorHandler(error, history);
  }

  const [customerUsersMenu, setCustomerUsersMenu] = React.useState([]);

  // Getting all Customer Users against the Company
  React.useEffect(() => {
    if (customerUsers) {
      setCustomerUsersMenu(
        customerUsers.map((item) => {
          return {
            key: item.Id,
            value: item.Email,
            label: item.Email,
          };
        })
      );
    }
  }, [customerUsers]);

  const areFieldsValidated = () => {
    if (
      isEmpty(fields.emails) !== null ||
      validateDate(fields.expiryDate, minDate, DefaultDate) !== null ||
      fields.numberOfParticipants === 0
    ) {
      return false;
    }
    return true;
  };

  //an api for getting emails
  const [
    {
      data: getRespondentsData,
      loading: respondentsLoading,
      error: errorInRespondents,
    },
    ExecuteGetRespondentsById,
  ] = useAxios(GetRespondentsbyId(inquiryId, token));

  // Getting Respondents Emails on Component Mounting
  React.useEffect(() => {
    ExecuteGetRespondentsById();
  }, []);

  // Getting Respondents data from API
  React.useEffect(() => {
    if (getRespondentsData) {
      // Filter for first dropdown: Status is "Pending" and RespondentStatus is null
      const pendingRespondents = getRespondentsData.data.filter(
        (item) => item.status === "Pending" && item.RespondentStatus === null
      );

      // Filter for second dropdown: Status is null and RespondentStatus is "Draft"
      const draftRespondents = getRespondentsData.data.filter(
        (item) => item.status === null && item.RespondentStatus === "Draft"
      );

      initialDraftEmailsRef.current = new Set(
        draftRespondents.map((item) => String(item.Email).toLowerCase())
      );

      const getRespondentsEmails = pendingRespondents.map((item) => {
        return {
          key: item.Id,
          value: item.Email,
          label: item.Email,
        };
      });

      const draftRespondentsEmails = draftRespondents.map((item) => {
        return {
          key: item.Id,
          value: item.Email,
          label: item.Email,
        };
      });

      if (getRespondentsEmails.length > 0) {
        setInquiryRespondents(getRespondentsEmails);
      } else {
        setInquiryRespondents([
          {
            key: 0,
            value: "No Respondents Added",
            label: "No Respondents Added",
          },
        ]);
      }

      // Set draft respondents in the second dropdown (fields.emails)
      if (draftRespondentsEmails.length > 0) {
        setFields((prevFields) => ({
          ...prevFields,
          emails: draftRespondentsEmails,
          numberOfParticipants: draftRespondentsEmails.length,
        }));
      }
    }
  }, [getRespondentsData]);
  // CALLING AN API FOR ADDING INQUIRY PARTICIPANTS
  const [
    { loading: isAddParticipantsLoading },
    executeAddInquiryParticipants,
  ] = useAxios(AddInquiryParticipantsUrl(inquiryId, token), { manual: true });

  // CALLING AN API FOR SAVING RESPONDENTS DRAFT
  const [
    { loading: isSaveDraftLoading },
    executeSaveRespondentsDraft,
  ] = useAxios(SaveRespondentsDraftUrl(inquiryId, token), { manual: true });

  // CALLING AN API FOR PARSING EMAILS FROM FILE
  const [{ loading: isParsingEmails }, executeParseEmails] = useAxios(
    { url: "", method: "GET" },
    { manual: true }
  );

  // CALLING AN API FOR UPLOADING BULK EMAIL FILE
  const [
    { loading: isUploadingFile },
    executeUploadBulkEmailFile,
  ] = useAxios(UploadBulkEmailFileUrl(token), { manual: true });

  const onSubmit = async () => {
    setValidateFields(true);
    if (areFieldsValidated()) {
      try {
        const data = {
          Participants: fields.emails.map((email) => email.value),
          NoOfParticipants: fields.numberOfParticipants,
          ExpiryDate: fields.expiryDate.toISOString().slice(0, -1),
          IsCustomerAdminRespondent: fields.isCustomerAdminRespondent,
        };
        console.log("data", data);
        await executeAddInquiryParticipants({ data: data });

        // Clear the second dropdown after successful submit
        setFields({
          emails: [],
          numberOfParticipants: 0,
          expiryDate: DefaultDate,
          isCustomerAdminRespondent: false,
        });

        // Refresh the respondents list to update UI
        await ExecuteGetRespondentsById();

        NotificationManager.success("Successfully Added Participants", 5000);
        history.push(`/customerAdmin/inquiry-logs`);
      } catch (exception) {
        console.log("Error in Adding Inquiry Participants", exception);
        apiErrorHandler(exception, history);
      }
    }
  };

  const onSaveDraft = async () => {
    setValidateFields(true);
    if (areFieldsValidated()) {
      try {
        const newParticipantEmails = fields.emails
          .filter(
            (email) =>
              !initialDraftEmailsRef.current.has(
                String(email.value).toLowerCase()
              )
          )
          .map((email) => email.value);

        const data = {
          Participants: newParticipantEmails,
          NoOfParticipants: newParticipantEmails.length,
          ExpiryDate: fields.expiryDate.toISOString().slice(0, -1),
          IsCustomerAdminRespondent: fields.isCustomerAdminRespondent,
        };
        console.log("data", data);
        await executeSaveRespondentsDraft({ data: data });

        // Refresh the respondents list to update UI
        await ExecuteGetRespondentsById();

        NotificationManager.success("Successfully Saved as Draft", 5000);
        history.push(`/customerAdmin/inquiry-logs`);
      } catch (exception) {
        console.log("Error in Saving Respondents Draft", exception);
        apiErrorHandler(exception, history);
      }
    }
  };

  const handleFileUpload = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();

    // ⚠️ Backend ke mutabiq ONE of these:
    formData.append("Files", file);
    // formData.append("File", file);

    for (let pair of formData.entries()) {
      console.log(pair[0], pair[1]);
    }

    const uploadResponse = await executeUploadBulkEmailFile({
      data: formData,
    });

    const BlobUrl = uploadResponse;

    console.log(BlobUrl);

    console.log(BlobUrl?.data?.data[0]);
    if (!BlobUrl) return;

    const parseResponse = await executeParseEmails(
      ParseEmailsUrl(BlobUrl?.data?.data[0], token)
    );

    console.log("Parse response:", parseResponse);

    // Access the data array from the response
    const emailsArray = parseResponse?.data?.data || [];

    const parsedEmails = emailsArray.map((email, index) => ({
      key: fields.emails.length + index,
      value: email,
      label: email,
    }));

    setFields({
      ...fields,
      emails: [...fields.emails, ...parsedEmails],
      numberOfParticipants: fields.emails.length + parsedEmails.length,
    });

    NotificationManager.success("Emails uploaded successfully!", 5000);
  };

  if (
    isAddParticipantsLoading ||
    isSaveDraftLoading ||
    isParsingEmails ||
    isUploadingFile
  ) {
    return (
      <Loading
        loading={
          isAddParticipantsLoading ||
          isSaveDraftLoading ||
          isParsingEmails ||
          isUploadingFile
        }
      />
    );
  }

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader>
            <TableHeader
              title="Manage Respondents"
              btnText="Submit"
              btnText2="Send email later"
              showBackButton
              onClick={() => onSubmit()}
              onClick2={() => onSaveDraft()}
              goBackRoute="/customerAdmin/inquiry-logs"
            />
          </CardHeader>
          <CardBody>
            {/* RESPONDENT EMAILS*/}
            <GridItem md={6} sm={12} xs={12}>
              <CustomSearchableSelect
                label="Respondents Emails"
                required
                multiple
                value={inquiryRespondents}
                disabled
              />
            </GridItem>

            {/* RESPONDENT EMAILS SEARCHABLE DROPDOWN */}
            <GridItem md={6} sm={12} xs={12}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <div style={{ flex: 1 }}>
                  <CustomSearchableSelect
                    label="Enter Respondents Emails"
                    required
                    multiple
                    value={fields.emails}
                    loading={loading}
                    menu={customerUsersMenu}
                    showCustomValues
                    inputValue={emailInput}
                    onInputChange={(val) => setEmailInput(val)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && emailInput) {
                        e.preventDefault();

                        if (validateEmail(emailInput) !== null) return;

                        const newEmail = {
                          key: Date.now(),
                          value: emailInput,
                          label: emailInput,
                        };

                        const exists = fields.emails.some(
                          (item) => item.value === emailInput
                        );

                        if (!exists) {
                          const updated = [...fields.emails, newEmail];
                          setFields({
                            ...fields,
                            emails: updated,
                            numberOfParticipants: updated.length,
                          });
                        }

                        setEmailInput(""); // clear input
                      }
                    }}
                    onChange={(newValue) => {
                      setFields({
                        ...fields,
                        emails: newValue,
                        numberOfParticipants: newValue.length,
                      });
                    }}
                    errorText={shouldValidateFields && isEmpty(fields.emails)}
                  />
                </div>
                <div
                  style={{
                    marginTop: "8px",
                  }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                  />
                  <Button
                    color="primary"
                    size="sm"
                    round
                    onClick={handleFileUpload}
                    disabled={isParsingEmails}
                    style={{ marginBottom: "40px" }}
                  >
                    <CloudUploadIcon style={{ marginRight: "5px" }} />
                    Upload
                  </Button>
                </div>
              </div>
            </GridItem>

            {/* EXPIRY DATE SELECTION CALENDAR */}
            <GridItem md={6} sm={12} xs={12}>
              <CustomDatePicker
                label="Expiration Date"
                required
                value={fields.expiryDate}
                minDate={minDate}
                onChange={(event) => {
                  setFields({ ...fields, expiryDate: event });
                }}
                errorText={
                  shouldValidateFields &&
                  validateDate(fields.expiryDate, minDate)
                }
              />
            </GridItem>
            {/* CUSTOMER ADMIN CHECKBOX */}
            <CustomSwitchLabel
              checked={fields.isCustomerAdminRespondent}
              onChange={(event) =>
                setFields({
                  ...fields,
                  isCustomerAdminRespondent: event.target.checked,
                })
              }
              label="Should customer admin receive survey form for this inquiry?"
              insideForm
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
};

export default SelectAndManageRespondents;

SelectAndManageRespondents.propTypes = {};
// isCustomerAdminRespondent;
